'use client';

import { defineMessage, FormattedMessage } from 'react-intl';
import { BaseStyleCardConfig, BaseStyleId, DynamicStyleCardConfig, DynamicStyleId, StaticStyleCardConfig, StaticStyleId } from '../types/Styles';
import CommunityActivityIcon from './layerImages/CommunityActivity.svg';
import CommunityWaypointsIcon from './layerImages/CommunityWaypoints.svg';
import DistanceMarkersIcon from './layerImages/DistanceMarkers.svg';
import DistanceMarkersMetricIcon from './layerImages/DistanceMarkersMetric.svg';
import LastSavedRouteIcon from './layerImages/LastSavedRoute.svg';
import NearbyTrailsIcon from './layerImages/NearbyTrails.svg';
import PhotosAlongTrailIcon from './layerImages/PhotosAlongTrail.svg';
import SurfaceTypeIcon from './layerImages/SurfaceType.svg';
import UserWaypointsIcon from './layerImages/UserWaypoints.svg';

export const baseStyleCardConfigs: Record<BaseStyleId, BaseStyleCardConfig> = {
  adminBasemap: {
    displayName: 'Custom basemap',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-adminBasemap@2x.png',
    key: 'adminBasemap',
    requiresAdmin: true,
    requiresPLP: true,
    requiresAdminStyleSourceUrl: true
  },
  alltrailsOutdoorsV2: {
    displayName: 'AllTrails',
    description: <FormattedMessage defaultMessage="Extensive trail & terrain map" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-layer-alltrailsOutdoorsV2@2x.png',
    key: 'alltrailsOutdoorsV2',
    supports3d: true
  },
  alltrailsOutdoorsV3: {
    displayName: 'AllTrails',
    description: <FormattedMessage defaultMessage="Extensive trail & terrain map" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-layer-alltrailsOutdoorsV2@2x.png',
    key: 'alltrailsOutdoorsV3',
    supports3d: true
  },
  alltrailsSatelliteV2: {
    displayName: defineMessage({ defaultMessage: 'Satellite' }),
    description: <FormattedMessage defaultMessage="Detailed satellite imagery" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-alltrailsSatellite@2x.png',
    key: 'alltrailsSatelliteV2',
    supports3d: true
  },
  alltrailsSatelliteMinimal: {
    displayName: 'Satellite minimal',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-alltrailsSatellite@2x.png',
    key: 'alltrailsSatelliteMinimal',
    supports3d: true
  },
  googleSatellite: {
    displayName: 'Google satellite',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-googleSatellite@2x.png',
    key: 'googleSatellite',
    requiresAdmin: true
  },
  ignPlan: {
    displayName: defineMessage({ defaultMessage: 'IGN Plan' }),
    description: <FormattedMessage defaultMessage="Trails, streets, & landmarks in France" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-ignPlan@2x.png',
    key: 'ignPlan',
    requiresAdmin: false
  },
  ignScan: {
    displayName: defineMessage({ defaultMessage: 'IGN SCAN 25' }),
    description: <FormattedMessage defaultMessage="Detailed topographic map of France" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-ignScan@2x.png',
    key: 'ignScan',
    requiresAdmin: false
  },
  opencycle: {
    displayName: 'OpenCycleMap (OCM)',
    description: <FormattedMessage defaultMessage="World map with cycling routes" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-opencycle-2@2x.png',
    key: 'opencycle',
    requiresAdmin: false
  },
  openstreet: {
    displayName: 'OpenStreetMap (OSM)',
    description: <FormattedMessage defaultMessage="World map with detailed landmarks" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-openstreet-2@2x.png',
    key: 'openstreet',
    requiresAdmin: false
  },
  osGreatBritain: {
    displayName: 'OS Great Britain',
    description: <FormattedMessage defaultMessage="Topography, trails, & public access" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-layer-osGreatBritain@2x.png',
    key: 'osGreatBritain',
    requiresAdmin: false,
    tier: 'peak',
    featureEntitlement: 'advanced_map_layers'
  },
  roadmap: {
    displayName: defineMessage({ defaultMessage: 'Road' }),
    description: <FormattedMessage defaultMessage="Streets & buildings" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-layer-roadmap@2x.png',
    key: 'roadmap'
  },
  topo: {
    displayName: defineMessage({ defaultMessage: 'U.S. Geological Survey (USGS)' }),
    description: <FormattedMessage defaultMessage="Historical topographic map of the U.S." />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-topo@2x.png',
    key: 'topo',
    requiresAdmin: false
  },
  worldparks: {
    displayName: defineMessage({ defaultMessage: 'Terrain' }),
    description: <FormattedMessage defaultMessage="Topo map with park boundaries" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-layer-worldparks@2x.png',
    key: 'worldparks'
  },
  worldtopo: {
    displayName: defineMessage({ defaultMessage: 'Terrain (legacy)' }),
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-worldtopo@2x.png',
    key: 'worldtopo',
    requiresAdmin: true
  }
};

export const staticStyleCardConfigs: Record<StaticStyleId, StaticStyleCardConfig> = {
  adminOverlay: {
    displayName: 'Custom overlay',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-admin-overlay@2x.png',
    key: 'adminOverlay',
    requiresAdmin: true,
    requiresPLP: true,
    requiresAdminStyleSourceUrl: true
  },
  waymarkedCycling: {
    displayName: defineMessage({ defaultMessage: 'Waymarked cycling' }),
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-waymarked-cycling@2x.png',
    key: 'waymarkedCycling',
    requires2d: true
  },
  waymarkedHiking: {
    displayName: defineMessage({ defaultMessage: 'Waymarked hiking' }),
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-waymarked-hiking@2x.png',
    key: 'waymarkedHiking',
    requires2d: true
  },
  waymarkedMTB: {
    displayName: defineMessage({ defaultMessage: 'Waymarked mountain biking' }),
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-waymarked-mtb@2x.png',
    key: 'waymarkedMTB',
    requires2d: true
  }
};

export const dynamicStyleCardConfigs: Record<DynamicStyleId, DynamicStyleCardConfig> = {
  after: {
    displayName: 'Last 60 days',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-after@2x.png',
    key: 'after',
    // PLAT-2212 trails:manage
    requiresAdmin: true,
    requiresPLP: true
  },
  clusterTrails: {
    displayName: 'Cluster trails',
    hasHeatmap: true,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-cluster-trails@2x.png',
    key: 'clusterTrails',
    requires2d: true,
    requiresAdmin: true
  },
  contribs: {
    displayName: 'Contrib VRs',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-contribs@2x.png',
    key: 'contribs',
    requires2d: true,
    requiresAdmin: true
  },
  communityActivity: {
    displayName: defineMessage({ defaultMessage: 'Community activity' }),
    imgSrc: CommunityActivityIcon,
    key: 'communityActivity'
  },
  communityWaypoints: {
    displayName: defineMessage({ defaultMessage: 'Community waypoints' }),
    imgSrc: CommunityWaypointsIcon,
    key: 'communityWaypoints'
  },
  distanceMarkers: {
    displayName: defineMessage({ defaultMessage: 'Distance markers' }),
    imgSrc: DistanceMarkersIcon,
    metricImgSrc: DistanceMarkersMetricIcon,
    adminImgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-distance-markers@2x.png',
    key: 'distanceMarkers'
  },
  fallFoliage: {
    displayName: defineMessage({ defaultMessage: 'U.S. fall foliage' }),
    description: <FormattedMessage defaultMessage="Fall color changes and forecast" />,
    featureEntitlement: 'fall_foliage',
    tier: 'plus',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-fall-foliage@2x.png',
    key: 'fallFoliage'
  },
  geoBoundaries: {
    displayName: 'Polygons',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-geo-boundaries@2x.png',
    key: 'geoBoundaries',
    requiresAdmin: true,
    requiresPLP: true
  },
  groundConditions: {
    displayName: defineMessage({ defaultMessage: 'Ground conditions' }),
    description: <FormattedMessage defaultMessage="Terrain & surface type" />,
    featureEntitlement: 'ground_conditions',
    tier: 'plus',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-ground-conditions.png',
    key: 'groundConditions'
  },
  heatmap: {
    displayName: defineMessage({ defaultMessage: 'Heatmap' }),
    description: <FormattedMessage defaultMessage="Recent trail activity" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-heatmap@2x.png',
    hideFromPLP: true,
    key: 'heatmap',
    requiresAdmin: true
  },
  highDefTerrain: {
    displayName: defineMessage({ defaultMessage: 'High-definition terrain' }),
    description: <FormattedMessage defaultMessage="LiDAR-powered details" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-lidar-hillshade@2x.png',
    key: 'highDefTerrain',
    tier: 'peak',
    featureEntitlement: 'advanced_map_layers',
    minzoom: 8
  },
  limit: {
    displayName: 'Most recent 50',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-limit@2x.png',
    key: 'limit',
    requires2d: true,
    // PLAT-2212 trails:manage
    requiresAdmin: true
  },
  mapsAll: {
    displayName: 'Maps all',
    hasHeatmap: true,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-maps-all@2x.png',
    key: 'mapsAll',
    requires2d: true,
    requiresAdmin: true
  },
  nearbyTrails: {
    displayName: defineMessage({ defaultMessage: 'Nearby trails' }),
    hasHeatmap: true,
    imgSrc: NearbyTrailsIcon,
    adminImgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-nearby-trails@2x.png',
    key: 'nearbyTrails'
  },
  newHeatmap: {
    displayName: defineMessage({ defaultMessage: 'Community heatmap' }),
    description: <FormattedMessage defaultMessage="Recent trail activity" />,
    featureEntitlement: 'community_heat_maps',
    tier: 'plus',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-communityHeatmap@2x.png',
    hideFromPLP: false,
    key: 'newHeatmap'
  },
  pendingTrails: {
    displayName: 'Pending trails',
    hasHeatmap: true,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-pending-trails@2x.png',
    key: 'pendingTrails',
    requiresAdmin: true
  },
  personalHeatmap: {
    displayName: defineMessage({ defaultMessage: 'Personal heatmap' }),
    description: <FormattedMessage defaultMessage="Your all-time trail activity" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/personal-heatmap.png',
    hideFromPLP: false,
    key: 'personalHeatmap'
  },
  photoHeatmap: {
    displayName: defineMessage({ defaultMessage: 'Photos' }),
    description: <FormattedMessage defaultMessage="Photo hotspots" />,
    hideAdminBadge: true,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-photos@2x.png',
    key: 'photoHeatmap',
    requiresAdmin: true,
    requiresPLP: true
  },
  photos: {
    displayName: defineMessage({ defaultMessage: 'Photos along the trail' }),
    imgSrc: PhotosAlongTrailIcon,
    adminImgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-photos@2x.png',
    key: 'photos'
  },
  photospot: {
    displayName: defineMessage({ defaultMessage: 'Photospots' }),
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-photospot@2x.png',
    key: 'photospot',
    requires2d: true,
    requiresAdmin: true
  },
  precipitation: {
    displayName: defineMessage({ defaultMessage: 'Precipitation' }),
    description: <FormattedMessage defaultMessage="Real-time intensity & forecast" />,
    featureEntitlement: 'precipitation',
    tier: 'plus',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-precipitation.png',
    key: 'precipitation'
  },
  privateLands: {
    displayName: defineMessage({ defaultMessage: 'U.S. private lands' }),
    description: <FormattedMessage defaultMessage="Private property boundaries" />,
    key: 'privateLands',
    featureEntitlement: 'advanced_map_layers',
    tier: 'peak',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-private-lands@2x.png',
    requiresAdmin: false,
    requires2d: true
  },
  publicLands: {
    displayName: defineMessage({ defaultMessage: 'U.S. public lands' }),
    description: <FormattedMessage defaultMessage="Land designations and managers" />,
    key: 'publicLands',
    featureEntitlement: 'advanced_map_layers',
    tier: 'peak',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-public-lands@2x.png',
    requiresAdmin: false,
    requires2d: true
  },
  recordingEnd: {
    displayName: defineMessage({ defaultMessage: 'Activity endpoints' }),
    description: <FormattedMessage defaultMessage="Popular endpoints" />,
    hideAdminBadge: true,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-recording-end@2x.png',
    key: 'recordingEnd',
    requiresAdmin: true,
    requiresPLP: true
  },
  recordingStart: {
    displayName: defineMessage({ defaultMessage: 'Activity starting points' }),
    description: <FormattedMessage defaultMessage="Popular starting points" />,
    hideAdminBadge: true,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-recording-start@2x.png',
    key: 'recordingStart',
    requiresAdmin: true,
    requiresPLP: true
  },
  recordingsAll: {
    displayName: 'Recordings all',
    hasHeatmap: true,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-recordings-all@2x.png',
    key: 'recordingsAll',
    requiresAdmin: true,
    requiresPLP: true
  },
  recordingsPop: {
    displayName: 'Recordings popular',
    hasHeatmap: true,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-recordings-pop@2x.png',
    key: 'recordingsPop',
    requiresAdmin: true
  },
  savedRoutePolyline: {
    displayName: defineMessage({ defaultMessage: 'Last saved route' }),
    featureEntitlement: 'build_your_own_trail',
    imgSrc: LastSavedRouteIcon,
    key: 'savedRoutePolyline',
    defaultEnabled: true
  },
  slopeAngle: {
    displayName: defineMessage({ defaultMessage: 'Slope angle' }),
    description: <FormattedMessage defaultMessage="Steepness of terrain" />,
    tier: 'peak',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-slope-angle@2x.png',
    key: 'slopeAngle',
    featureEntitlement: 'advanced_map_layers',
    minzoom: 8
  },
  snowDepth: {
    displayName: defineMessage({ defaultMessage: 'Snow depth' }),
    description: <FormattedMessage defaultMessage="Accumulated snowfall" />,
    featureEntitlement: 'snow_depth',
    tier: 'plus',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-snow-depth.png',
    key: 'snowDepth'
  },
  surfaceType: {
    displayName: defineMessage({ defaultMessage: 'Surface type' }),
    imgSrc: SurfaceTypeIcon,
    key: 'surfaceType'
  },
  temperature: {
    displayName: defineMessage({ defaultMessage: 'Temperature' }),
    description: <FormattedMessage defaultMessage="Fluctuations along the trail" />,
    featureEntitlement: 'temperature',
    tier: 'plus',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-temperature.png',
    key: 'temperature'
  },
  trailHeatmap: {
    displayName: defineMessage({ defaultMessage: 'Trail heatmap (Admin only)' }),
    description: <FormattedMessage defaultMessage="AllTrails trail network" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-communityHeatmap@2x.png',
    hideFromPLP: true,
    key: 'trailHeatmap',
    requiresAdmin: true
  },
  userWaypoints: {
    displayName: defineMessage({ defaultMessage: 'Your waypoints' }),
    imgSrc: UserWaypointsIcon,
    key: 'userWaypoints'
  },
  waypoints: {
    displayName: defineMessage({ defaultMessage: 'Waypoints' }),
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-waypoints@2x.png',
    key: 'waypoints'
  },
  wildfire: {
    displayName: defineMessage({ defaultMessage: 'Wildfires' }),
    description: <FormattedMessage defaultMessage="Active & recent wildfire locations" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-wildfire@2x.png',
    key: 'wildfire',
    requires2d: true
  }
};
